<template>
  <div class="checkpoint-microlearning-cell" data-testid="checkpoint-microlearning-cell">
    <!-- Video types: thumbnail + label (or Processing… / context mismatch) -->
    <template v-if="isVideoType">
      <div class="checkpoint-microlearning-cell__thumb" aria-hidden="true">
        <svg
          class="checkpoint-microlearning-cell__thumb-svg"
          width="48"
          height="28"
          viewBox="0 0 48 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="48" height="28" rx="6" fill="#E4E7EC" />
          <path d="M20.125 20.0909V9.18182L29.3977 14.6364L20.125 20.0909Z" fill="white" />
        </svg>
      </div>
      <span
        class="checkpoint-microlearning-cell__label"
        :class="`checkpoint-microlearning-cell__label--${tone}`"
      >
        <template v-if="showContextMismatch">
          <span>{{ labelPrefix }}</span>
          <svg
            class="checkpoint-microlearning-cell__warn-icon"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6.134 1.75c.385-.666 1.347-.666 1.732 0l5.196 9c.385.667-.096 1.5-.866 1.5H1.804c-.77 0-1.251-.833-.866-1.5l5.196-9z"
              fill="#D92D20"
            />
            <path
              d="M7 5v3.25M7 10.25h.01"
              stroke="#fff"
              stroke-width="1.25"
              stroke-linecap="round"
            />
          </svg>
          <span>{{ labelSuffix }}</span>
        </template>
        <template v-else>{{ label }}</template>
      </span>
    </template>

    <!-- Article: doc icon + label (or empty-article error) -->
    <template v-else-if="isArticleType">
      <div class="checkpoint-microlearning-cell__doc" aria-hidden="true">
        <svg
          class="checkpoint-microlearning-cell__doc-glyph"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.667 4h10.666M2.667 8h10.666M2.667 12h10.666"
            stroke="#667085"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      </div>
      <span
        class="checkpoint-microlearning-cell__label"
        :class="`checkpoint-microlearning-cell__label--${tone}`"
      >
        {{ label }}
      </span>
    </template>

    <!-- Shape 4 / no microlearning: text only (Figma: Inspector Only) -->
    <span
      v-else
      class="checkpoint-microlearning-cell__label"
      data-testid="checkpoint-microlearning-cell-empty"
    >
      {{ emptyLabel }}
    </span>
  </div>
</template>

<script>
/** Module scope: safe to use in hoisted `defineProps()` validators. */
export const MICROLEARNING_TYPES = ["VIDEO_WITH_TOC", "VIDEO_STEP_BY_STEP_TUTORIAL", "ARTICLE"];

export function isMicrolearningType(v) {
  return v === null || MICROLEARNING_TYPES.includes(v);
}

export function isVideoStatus(v) {
  return v === "PROCESSING" || v === "READY";
}
</script>

<script setup>
import { computed } from "vue";

const props = defineProps({
  /** Stable enum key — never a display string. null = Shape 4 (no microlearning). */
  microlearningType: {
    type: String,
    default: null,
    validator: isMicrolearningType,
  },
  /** Only relevant for VIDEO_* types. */
  videoStatus: {
    type: String,
    default: "READY",
    validator: isVideoStatus,
  },
  /** Only relevant for ARTICLE — empty body treatment. */
  isEmptyArticle: {
    type: Boolean,
    default: false,
  },
  /** Error-colored type label (and context-mismatch copy for video). */
  isContextMismatch: {
    type: Boolean,
    default: false,
  },
});

const VIDEO_LABELS = {
  VIDEO_WITH_TOC: "Video · TOC",
  VIDEO_STEP_BY_STEP_TUTORIAL: "Video · Tutorial",
};

const emptyLabel = "Inspector Only";

const isVideoType = computed(() =>
  ["VIDEO_WITH_TOC", "VIDEO_STEP_BY_STEP_TUTORIAL"].includes(props.microlearningType)
);

const isArticleType = computed(() => props.microlearningType === "ARTICLE");

const isProcessing = computed(() => isVideoType.value && props.videoStatus === "PROCESSING");

const showContextMismatch = computed(
  () => isVideoType.value && !isProcessing.value && props.isContextMismatch
);

const typeLabel = computed(() => VIDEO_LABELS[props.microlearningType] || "");

const label = computed(() => {
  if (isArticleType.value) {
    if (props.isEmptyArticle) return "Article · empty";
    return "Article";
  }
  if (isProcessing.value) return "Processing…";
  return typeLabel.value;
});

const labelPrefix = computed(() => `${typeLabel.value} ·`);
const labelSuffix = "Context mismatch";

const tone = computed(() => {
  if (isArticleType.value) {
    if (props.isEmptyArticle || props.isContextMismatch) return "error";
    return "default";
  }
  if (isVideoType.value) {
    if (isProcessing.value) return "warning";
    if (props.isContextMismatch) return "error";
    return "default";
  }
  return "default";
});
</script>

<style scoped>
.checkpoint-microlearning-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.checkpoint-microlearning-cell__thumb {
  flex: 0 0 auto;
  width: 48px;
  height: 28px;
  line-height: 0;
}

.checkpoint-microlearning-cell__thumb-svg {
  display: block;
}

.checkpoint-microlearning-cell__doc {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fcfcfd;
  box-sizing: border-box;
}

.checkpoint-microlearning-cell__doc-glyph {
  display: block;
}

.checkpoint-microlearning-cell__label {
  min-width: 0;
  flex: 1 1 auto;
  overflow: visible;
  white-space: nowrap;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.checkpoint-microlearning-cell__warn-icon {
  flex-shrink: 0;
  display: block;
}

.checkpoint-microlearning-cell__label--warning {
  color: #b54708;
}

.checkpoint-microlearning-cell__label--error {
  color: #b42318;
}
</style>
